import { type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  ReceiptText, Clock, MessageCircle, LogOut,
  Sun, Moon, Home,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useTimer } from '@/context/TimerContext';
import { useUnreadCount } from '@/features/communication/api';
import { formatElapsed } from '@/lib/utils';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: ReactNode;
  title?: string;
}

export function Layout({ children, title }: LayoutProps) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { activeEntry, elapsed } = useTimer();
  const { data: unread } = useUnreadCount();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-dvh flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 safe-top">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-bold text-gray-900 dark:text-white">
              {title || 'Merkos'}
            </h1>
            {user && (
              <p className="text-xs text-gray-500 dark:text-gray-400">{user.name}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {activeEntry && (
              <div className="mr-2 flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
                {formatElapsed(elapsed)}
              </div>
            )}
            <button
              onClick={toggleTheme}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <button
              onClick={handleLogout}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              aria-label="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        {children}
      </main>

      {/* Bottom navigation */}
      <nav className="flex-shrink-0 border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 safe-bottom">
        <div className="flex items-center">
          <NavItem to="/" icon={<Home className="h-5 w-5" />} label="Home" />
          <NavItem to="/expenses" icon={<ReceiptText className="h-5 w-5" />} label="Expenses" />
          <NavItem to="/time" icon={<Clock className="h-5 w-5" />} label="Time" />
          <NavItem
            to="/messages"
            icon={
              <div className="relative">
                <MessageCircle className="h-5 w-5" />
                {(unread?.unread_count ?? 0) > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                    {unread!.unread_count > 9 ? '9+' : unread!.unread_count}
                  </span>
                )}
              </div>
            }
            label="Messages"
          />
        </div>
      </nav>
    </div>
  );
}

function NavItem({ to, icon, label }: { to: string; icon: ReactNode; label: string }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors',
          isActive
            ? 'text-blue-600 dark:text-blue-400'
            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

export function PageHeader({ title, onBack }: { title: string; onBack?: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
      {onBack !== undefined && (
        <button
          onClick={onBack ?? (() => navigate(-1))}
          className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
          aria-label="Go back"
        >
          ←
        </button>
      )}
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
    </div>
  );
}
